import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { KineticLines, Reveal } from "@/components/Reveal";
import { IMG } from "@/lib/images";
import { track } from "@/lib/track";

const PILLARS = [
  { no: "01", title: "Distinctive Brand", copy: "An identity people photograph, remember and talk about." },
  { no: "02", title: "Product Innovation", copy: "A menu built on real ingredients and seasonal thinking." },
  { no: "03", title: "Scalable Store Format", copy: "A bold, compact retail format designed to grow." },
  { no: "04", title: "Central Brand Support", copy: "Brand, product and marketing support from the centre." },
];

export default function FranchiseTeaser() {
  return (
    <section className="bg-deep py-16 md:py-24" data-testid="franchise-teaser-section">
      <div className="mx-auto grid max-w-[1440px] items-center gap-12 px-5 md:px-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-orange-rific md:text-xs">Franchise</p>
          </Reveal>
          <KineticLines
            lines={["BRING RIFIC", "TO YOUR CITY."]}
            className="mt-3 block"
            lineClassName="font-display text-[clamp(2rem,4.2vw,3.25rem)] font-extrabold leading-[1.05] tracking-[-0.01em] text-cream"
          />
          <Reveal delay={0.18}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-cream/80 md:text-lg">
              We're building an ingredient-led ice cream and dessert brand with a distinctive retail identity designed
              to grow across markets.
            </p>
          </Reveal>

          <Reveal delay={0.22}>
            <div className="mt-7 grid gap-4 sm:grid-cols-2" data-testid="franchise-teaser-offer">
              <div data-testid="franchise-teaser-founding10" className="rounded-2xl bg-orange-rific p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/85">Founding 10</p>
                <p className="mt-2 font-display text-3xl font-extrabold leading-none text-white">₹0 FRANCHISE FEE</p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.16em] text-white/85">
                  First 10 approved franchise partners.
                </p>
              </div>
              <div className="rounded-2xl border border-deep-line bg-deep-soft p-6" data-testid="franchise-teaser-model">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-orange-rific">RIFIC Parlour</p>
                    <p className="mt-2 font-display text-xl font-extrabold text-cream md:text-2xl">200–300 SQ. FT.</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-cream/50">Indicative Investment</p>
                    <p className="mt-2 font-display text-xl font-extrabold text-cream md:text-2xl">UP TO ₹8 LAKH*</p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          <div className="mt-8 grid gap-px overflow-hidden rounded-2xl bg-deep-line sm:grid-cols-2" data-testid="franchise-reasons">
            {PILLARS.map((r, i) => (
              <Reveal key={r.no} delay={i * 0.07}>
                <div className="h-full bg-deep p-6 transition-colors duration-300 hover:bg-deep-soft md:p-7">
                  <p className="font-mono text-xs text-orange-rific">{r.no}</p>
                  <h3 className="mt-3 font-display text-xl font-bold uppercase tracking-[-0.01em] text-cream md:text-2xl">
                    {r.title}
                  </h3>
                  <p className="mt-2 text-base leading-relaxed text-cream/65">{r.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2} className="mt-10">
            <Link
              to="/franchise#franchise-form"
              data-testid="franchise-teaser-cta"
              onClick={() => track("franchise_cta_click", { placement: "home_teaser", campaign: "founding_10" })}
              className="inline-flex h-14 items-center gap-2 rounded-full bg-orange-rific px-9 text-sm font-bold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:bg-cream hover:text-deep"
            >
              Apply for the Founding 10
              <ArrowUpRight className="h-4 w-4" />
            </Link>
            <p className="mt-4 max-w-xl text-xs leading-relaxed text-cream/45" data-testid="franchise-teaser-disclaimer">
              *Indicative planning estimate; actual investment may vary. The Founding 10 fee waiver applies only to
              the first 10 franchise partners formally approved by RIFIC.
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-5">
          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-[28px] bg-cream">
              <img
                src={IMG.storefrontPortrait}
                alt="RIFIC orange storefront with rounded arch entrance"
                loading="lazy"
                className="h-auto w-full object-contain"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
